import { get, post, put, remove } from '../api';
import type {
  ClaimLdapDomainRequest,
  ClaimSamlDomainRequest,
  UpdateOrganizationDefaultReportCustomTextRequest,
  UpdateOrganizationDefaultReportCustomTextResponse,
  UpdateSamlRequest,
} from '../../../typings/api';
import type { AsyncStatus, Organization, Task } from '../../types';

export type UpdateDefaultReportCustomTextMeta = { organizationId: number };

export const actionTypes = Object.freeze({
  // Audit report
  CREATE_ORG_AUDIT_REPORT: 'CREATE_ORG_AUDIT_REPORT',
  // Auth
  FETCH_PENDING_ORG_DOMAINS: 'FETCH_PENDING_ORG_DOMAINS',
  FETCH_ORG_DOMAINS: 'FETCH_ORG_DOMAINS',
  DELETE_ORG_DOMAIN: 'DELETE_ORG_DOMAIN',
  UPDATE_SAML: 'UPDATE_SAML',
  DELETE_SAML: 'DELETE_SAML',
  CLAIM_SAML_DOMAIN: 'CLAIM_SAML_DOMAIN',
  CLAIM_LDAP_DOMAIN: 'CLAIM_LDAP_DOMAIN',
  RESEND_DOMAIN_VERIFICATION_EMAIL: 'RESEND_DOMAIN_VERIFICATION_EMAIL',
  SET_SSO_CONFIGURATION_STATUS: 'SET_SSO_CONFIGURATION_STATUS',
  // Org details
  FETCH_ONE_ORGANIZATION: 'FETCH_ONE_ORGANIZATION',
  UPDATE_ORGANIZATION: 'UPDATE_ORGANIZATION',
  UPDATE_ORGANIZATION_DEFAULT_REPORT_CUSTOM_TEXT: 'UPDATE_ORGANIZATION_DEFAULT_REPORT_CUSTOM_TEXT',
  // Org labels
  FETCH_ORG_LABELS: 'FETCH_ORG_LABELS',
  CREATE_ORG_LABEL: 'CREATE_ORG_LABEL',
  DELETE_ORG_LABEL: 'DELETE_ORG_LABEL',
});

const organizationActions = {
  fetchOne: (orgId: number) => ({
    type: actionTypes.FETCH_ONE_ORGANIZATION,
    meta: {
      id: orgId,
    },
    async payload() {
      return get(`/api/organizations/${orgId}`, true);
    },
  }),
  update: (org: Organization) => ({
    type: actionTypes.UPDATE_ORGANIZATION,
    async payload() {
      return put(`/api/organizations/${org.id}`, org);
    },
  }),
  createAuditReport: (teamIds?: number[] | 'null') => ({
    type: actionTypes.CREATE_ORG_AUDIT_REPORT,
    async payload() {
      return get<Task>(`/api/organization/summary`, false, { teamId: teamIds });
    },
  }),
  fetchPendingOrganizationDomains: (organizationId: number) => ({
    type: actionTypes.FETCH_PENDING_ORG_DOMAINS,
    async payload() {
      return get(`/api/organizations/${organizationId}/pending-sso-domains`);
    },
  }),
  fetchOrganizationDomains: (organizationId: number) => ({
    type: actionTypes.FETCH_ORG_DOMAINS,
    async payload() {
      return get(`/api/organizations/${organizationId}/domains`);
    },
  }),
  deleteOrganizationDomain: (organizationId: number, domain: string) => ({
    type: actionTypes.DELETE_ORG_DOMAIN,
    meta: { domain },
    async payload() {
      return remove(`/api/organizations/${organizationId}/domains/${domain}`);
    },
  }),
  updateSamlConfiguration: (organizationId: number, reqBody: UpdateSamlRequest) => ({
    type: actionTypes.UPDATE_SAML,
    async payload() {
      return put(`/api/organizations/${organizationId}/saml`, reqBody);
    },
  }),
  deleteSamlConfiguration: (organizationId: number) => ({
    type: actionTypes.DELETE_SAML,
    async payload() {
      return remove(`/api/organizations/${organizationId}/saml`);
    },
  }),
  claimSamlDomain: (organizationId: number, reqBody: ClaimSamlDomainRequest) => ({
    type: actionTypes.CLAIM_SAML_DOMAIN,
    async payload() {
      return put(`/api/organizations/${organizationId}/saml-domain`, reqBody);
    },
  }),
  claimLdapDomain: (organizationId: number, reqBody: ClaimLdapDomainRequest) => ({
    type: actionTypes.CLAIM_LDAP_DOMAIN,
    async payload() {
      return post(`/api/organizations/${organizationId}/ldap-domain`, reqBody);
    },
  }),
  resendDomainVerificationEmail: (organizationId: number, tokenId: number) => ({
    type: actionTypes.RESEND_DOMAIN_VERIFICATION_EMAIL,
    meta: { tokenId },
    async payload() {
      return put(`/api/organizations/${organizationId}/resend-domain-verification-email/${tokenId}`);
    },
  }),
  setSsoConfigurationStatus: (status: Partial<AsyncStatus>) => ({
    type: actionTypes.SET_SSO_CONFIGURATION_STATUS,
    payload: status,
  }),
  fetchOrgLabels: () => ({
    type: actionTypes.FETCH_ORG_LABELS,
    async payload() {
      return get('/api/organizations/labels');
    },
  }),
  createOrgLabel: (label: string) => ({
    type: actionTypes.CREATE_ORG_LABEL,
    async payload() {
      return post('/api/organizations/labels', { label });
    },
  }),
  deleteOrgLabel: (id: number) => ({
    type: actionTypes.DELETE_ORG_LABEL,
    meta: { id },
    async payload() {
      return remove(`/api/organizations/labels/${id}`);
    },
  }),
  updateDefaultReportCustomText: (
    organizationId: number,
    requestData: UpdateOrganizationDefaultReportCustomTextRequest,
  ) => ({
    type: actionTypes.UPDATE_ORGANIZATION_DEFAULT_REPORT_CUSTOM_TEXT,
    meta: { organizationId },
    async payload() {
      return put<UpdateOrganizationDefaultReportCustomTextRequest, UpdateOrganizationDefaultReportCustomTextResponse>(
        `/api/organizations/${organizationId}/defaultReportCustomText`,
        requestData,
      );
    },
  }),
};

export default organizationActions;
